import {format} from 'date-fns';
import {Calendar, CheckCircle, Clock} from 'lucide-react';
import {Badge} from '@common-web/components/ui/badge';
import {CBS_ACTIVE_PILL_CLASS} from '@common-web/components/account/cbsAccountsPresentation';
import {DataTable, useDataTable, type Column} from '@common-web/components/data-table';
import {ListPaginationFooter} from '@common-web/components/list-pagination-footer';
import {trpc, RouterOutput} from '@common-web/lib/trpc';

type SubscriberInvoice =
  RouterOutput['internal']['invoice']['listBySubscriber']['invoices'][number];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(value);

const formatDate = (date: Date | null | undefined) => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'MM/dd/yyyy');
  } catch {
    return 'N/A';
  }
};

function statusBadge(status: string) {
  if (status === 'CLOSED') {
    return (
      <Badge className={`border-0 font-semibold ${CBS_ACTIVE_PILL_CLASS}`}>
        <CheckCircle className='h-3 w-3 mr-1' />
        Closed
      </Badge>
    );
  }
  if (status === 'OPEN') {
    return (
      <Badge className='border-0 bg-[#FEF3C7] font-semibold text-[#B45309]'>
        <Clock className='h-3 w-3 mr-1' />
        Open
      </Badge>
    );
  }
  return (
    <Badge className='bg-brand-gray/10 text-brand-gray border-brand-gray/20'>
      {status || 'Unknown'}
    </Badge>
  );
}

/**
 * Invoicing history for a single subscriber. Rendered inside the shared
 * GroupMemberDetails page (internal portal only). Lists invoices where the
 * subscriber was billed, with their premium share, and deep-links into the
 * invoice detail with the subscriber breakdown sheet auto-opened.
 */
export function MemberInvoicingTab({subscriberId}: {subscriberId: string}) {
  const table = useDataTable({defaultPageSize: 20, syncToUrl: 'invoices'});

  const {data, isLoading, error} = trpc.internal.invoice.listBySubscriber.useQuery(
    {subscriberId, page: table.page, pageSize: table.pageSize},
    {enabled: !!subscriberId},
  );

  const columns: Column<SubscriberInvoice>[] = [
    {
      id: 'invoiceNumber',
      header: 'Invoice #',
      render: (inv) => (
        <span className='font-mono font-medium text-primary'>
          {inv.invoiceNumber || `#${inv.invoiceKey}`}
        </span>
      ),
    },
    {
      id: 'group',
      header: 'Group',
      render: (inv) => (
        <div className='flex flex-col'>
          <span className='text-sm text-foreground'>{inv.groupName}</span>
          {inv.clientName && (
            <span className='text-xs text-muted-foreground'>{inv.clientName}</span>
          )}
        </div>
      ),
    },
    {
      id: 'period',
      header: 'Period',
      render: (inv) => (
        <div className='flex items-center gap-2'>
          <Calendar className='h-4 w-4 text-muted-foreground' />
          <span className='text-sm'>
            {formatDate(inv.billingFrom)} - {formatDate(inv.billingThru)}
          </span>
        </div>
      ),
    },
    {
      id: 'subscriberPremium',
      header: 'Member Premium',
      align: 'right',
      render: (inv) => (
        <span className='font-medium tabular-nums text-foreground'>
          {formatCurrency(inv.subscriberPremium)}
        </span>
      ),
    },
    {
      id: 'invoicePremium',
      header: 'Invoice Total',
      align: 'right',
      render: (inv) => (
        <span className='text-sm tabular-nums text-muted-foreground'>
          {formatCurrency(inv.invoicePremium)}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      align: 'right',
      render: (inv) => statusBadge(inv.invoiceStatus),
    },
  ];

  return (
    <div className='rounded-xl border bg-card p-5'>
      <h3 className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
        Invoicing
      </h3>
      <p className='mb-3 mt-1 text-[12px] text-muted-foreground'>
        Invoices this member has been billed on — click a row to see this member&apos;s billed lines
        for that invoice. For researching VBA invoice discrepancies.
      </p>
      <DataTable<SubscriberInvoice>
        columns={columns}
        data={data?.invoices}
        loading={isLoading}
        error={error ? new Error(error.message) : null}
        emptyMessage='No invoices found for this member'
        rowKey={(inv) => String(inv.invoiceKey)}
        rowHref={(inv) => `/invoices/${inv.invoiceKey}?subscriberId=${subscriberId}`}
      />
      {data && data.totalPages > 1 && (
        <div className='-mx-5 -mb-5 mt-3 overflow-hidden rounded-b-xl'>
          <ListPaginationFooter
            page={table.page}
            pageSize={table.pageSize}
            total={data.totalCount}
            totalPages={data.totalPages}
            onPageChange={table.setPage}
            rangeEntityLabel='invoices'
          />
        </div>
      )}
    </div>
  );
}
